import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import "@/styles/mobile-optimizations.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

// Core pages (loaded immediately)
import ChatPage from "./pages/ChatPage";
import HomePage from "./pages/HomePage";

// Corporate Login (lazy loaded)
const CorporateLogin = lazy(() => import("./pages/CorporateLogin"));

// Marketing pages (lazy loaded)
const LandingPageNew = lazy(() => import("./pages/marketing/LandingPageNew"));
const ProductsPage = lazy(() => import("./pages/marketing/ProductsPage"));
const PricingPage = lazy(() => import("./pages/marketing/PricingPage"));
const EnterprisePage = lazy(() => import("./pages/marketing/EnterprisePage"));
const InstitutionalRegister = lazy(() => import("./pages/marketing/InstitutionalRegister"));
const SafetyPage = lazy(() => import("./pages/marketing/SafetyPage"));
const AboutPage = lazy(() => import("./pages/marketing/AboutPage"));

// Legacy marketing pages
const InstitutionalPage = lazy(() => import("./pages/InstitutionalPage"));

// App pages (lazy loaded)
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const UserDetailDashboard = lazy(() => import("./pages/UserDetailDashboard"));
const ParentDashboard = lazy(() => import("./pages/ParentDashboard"));
const ParentLoginPage = lazy(() => import("./pages/ParentLoginPage"));
const ApproveParentLinkPage = lazy(() => import("./pages/ApproveParentLinkPage"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const SubscriptionSuccessPage = lazy(() => import("./pages/SubscriptionSuccessPage"));
const SubscriptionCancelPage = lazy(() => import("./pages/SubscriptionCancelPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const ChildPrivacyPolicy = lazy(() => import("./pages/ChildPrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const KVKKPolicy = lazy(() => import("./pages/KVKKPolicy"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const StudyDashboard = lazy(() => import("./pages/StudyDashboard"));
const StudentChatPage = lazy(() => import("./pages/StudentChatPage"));
const TestPage = lazy(() => import("./pages/TestPage"));
const FlashcardPage = lazy(() => import("./pages/FlashcardPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const DocumentUploadPage = lazy(() => import("./pages/DocumentUploadPage"));
const DocumentDetailPage = lazy(() => import("./pages/DocumentDetailPage"));
const ExamQuestionsPage = lazy(() => import("./pages/ExamQuestionsPage"));
const ExamPage = lazy(() => import("./pages/ExamPage"));
const AdminExamBank = lazy(() => import("./pages/AdminExamBank"));
const TeacherPanel = lazy(() => import("./pages/TeacherPanel"));
const StudyAdminPanel = lazy(() => import("./pages/StudyAdminPanel"));
const LessonViewer = lazy(() => import("./components/study/LessonViewer"));
const LibraryPage = lazy(() => import("./pages/LibraryPage"));
const LibraryDetailPage = lazy(() => import("./pages/LibraryDetailPage"));
const LibrarySearchPage = lazy(() => import("./pages/LibrarySearchPage"));
const StudyPlanner = lazy(() => import("./pages/StudyPlanner"));
const AdvancedAnalytics = lazy(() => import("./pages/AdvancedAnalytics"));
const CreditUsagePage = lazy(() => import("./pages/CreditUsagePage"));
const ExamMode = lazy(() => import("./pages/ExamMode"));
const ExamResults = lazy(() => import("./pages/ExamResults"));
const CollaborativeSession = lazy(() => import("./pages/CollaborativeSession"));
const ArenaPage = lazy(() => import("./pages/ArenaPage"));
const StudyTopics = lazy(() => import("./pages/StudyTopics"));
const StudyProfile = lazy(() => import("./pages/StudyProfile"));
const ZehaMemoryPage = lazy(() => import("./pages/ZehaMemoryPage"));
const StudyResearchPage = lazy(() => import("./pages/StudyResearchPage"));
const TopicDetailPage = lazy(() => import("./pages/TopicDetailPage"));
const ExamPredictions = lazy(() => import("./pages/ExamPredictions"));

// Content Pipeline pages (lazy loaded)
const ContentPipelineDashboard = lazy(() => import("./pages/ContentPipelineDashboard"));
const ContentPipelineDetail = lazy(() => import("./pages/ContentPipelineDetail"));
const ContentPipelineReview = lazy(() => import("./pages/ContentPipelineReview"));
const ContentPipelineCoverage = lazy(() => import("./pages/ContentPipelineCoverage"));

// Institutional / Organization pages (lazy loaded)
const SchoolAdminDashboard = lazy(() => import("./pages/school-admin/SchoolAdminDashboard"));
const StudentManagement = lazy(() => import("./pages/school-admin/StudentManagement"));
const TeacherManagement = lazy(() => import("./pages/school-admin/TeacherManagement"));
const ClassManagement = lazy(() => import("./pages/school-admin/ClassManagement"));
const OrgAnalytics = lazy(() => import("./pages/school-admin/OrgAnalytics"));
const OrgSettings = lazy(() => import("./pages/school-admin/OrgSettings"));
const SchoolAdminMessages = lazy(() => import("./pages/school-admin/Messages"));
const TeacherDashboard = lazy(() => import("./pages/teacher/TeacherDashboard"));
const ClassDetail = lazy(() => import("./pages/teacher/ClassDetail"));
const TeacherMessages = lazy(() => import("./pages/teacher/Messages"));
const TeacherAssignments = lazy(() => import("./pages/teacher/Assignments"));
const ParentMessages = lazy(() => import("./pages/parent/Messages"));

// Teacher Content Management pages (lazy loaded)
const ContentDashboard = lazy(() => import("./pages/teacher/content/ContentDashboard"));
const NotesManager = lazy(() => import("./pages/teacher/content/NotesManager"));
const QuestionBank = lazy(() => import("./pages/teacher/content/QuestionBank"));
const ExamBuilder = lazy(() => import("./pages/teacher/content/ExamBuilder"));
const FlashcardBuilder = lazy(() => import("./pages/teacher/content/FlashcardBuilder"));

import OrgProtectedRoute from "./components/org/OrgProtectedRoute";

import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Loading fallback
const PageLoader = () => (
  <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#fafbff'}}>
    <div style={{textAlign:'center'}}>
      <div style={{fontSize:'32px',marginBottom:'12px',animation:'pulse 1.5s infinite'}}>📚</div>
      <div style={{color:'#667eea',fontWeight:600,fontSize:'14px'}}>Yükleniyor...</div>
    </div>
  </div>
);

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <div className="App">
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ===== MARKETING PAGES (public website) ===== */}
            <Route path="/" element={<LandingPageNew />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/enterprise" element={<EnterprisePage />} />
            <Route path="/enterprise/register" element={<InstitutionalRegister />} />
            <Route path="/safety" element={<SafetyPage />} />
            <Route path="/about" element={<AboutPage />} />
            
            {/* Legacy marketing routes (redirect) */}
            <Route path="/kurumsal" element={<Navigate to="/enterprise" replace />} />
            <Route path="/institutional" element={<Navigate to="/enterprise" replace />} />
            <Route path="/about-us" element={<Navigate to="/about" replace />} />

            {/* ===== APPLICATION PAGES (/app/*) ===== */}
            <Route path="/app" element={<ChatPage />} />
            <Route path="/app/study" element={<StudyDashboard />} />
            <Route path="/app/study/topics" element={<StudyTopics />} />
            <Route path="/app/study/topic/:topicId" element={<TopicDetailPage />} />
            <Route path="/app/study/profile" element={<StudyProfile />} />
            <Route path="/app/study/chat" element={<StudentChatPage />} />
            <Route path="/app/study/tests" element={<TestPage />} />
            <Route path="/app/study/flashcards" element={<FlashcardPage />} />
            <Route path="/app/study/analytics" element={<AnalyticsPage />} />
            <Route path="/app/study/advanced-analytics" element={<AdvancedAnalytics />} />
            <Route path="/app/study/credits" element={<CreditUsagePage />} />
            <Route path="/app/study/planner" element={<StudyPlanner />} />
            <Route path="/app/study/exam-mode" element={<ExamMode />} />
            <Route path="/app/study/exam-results/:examId" element={<ExamResults />} />
            <Route path="/app/study/collaborate/:sessionId?" element={<CollaborativeSession />} />
            <Route path="/app/study/arena" element={<ArenaPage />} />
            <Route path="/app/study/documents" element={<DocumentUploadPage />} />
            <Route path="/app/study/documents/:documentId" element={<DocumentDetailPage />} />
            <Route path="/app/study/memory" element={<ZehaMemoryPage />} />
            <Route path="/app/study/research" element={<StudyResearchPage />} />
            <Route path="/app/study/exam-questions" element={<ExamQuestionsPage />} />
            <Route path="/app/study/exam" element={<ExamPage />} />
            <Route path="/app/study/exam-predictions" element={<ExamPredictions />} />
            <Route path="/app/study/lesson/:topicId" element={<LessonViewer />} />
            <Route path="/app/library" element={<LibraryPage />} />
            <Route path="/app/library/search" element={<LibrarySearchPage />} />
            <Route path="/app/library/:id" element={<LibraryDetailPage />} />
            <Route path="/app/parent" element={<ParentDashboard />} />
            <Route path="/app/subscription" element={<SubscriptionPage />} />
            <Route path="/app/subscription/success" element={<SubscriptionSuccessPage />} />
            <Route path="/app/subscription/cancel" element={<SubscriptionCancelPage />} />

            {/* Legacy app routes (redirect to /app/*) */}
            <Route path="/chat" element={<Navigate to="/app" replace />} />
            <Route path="/study" element={<Navigate to="/app/study" replace />} />
            <Route path="/study/*" element={<Navigate to="/app/study" replace />} />
            <Route path="/library" element={<Navigate to="/app/library" replace />} />
            <Route path="/parent-dashboard" element={<Navigate to="/app/parent" replace />} />
            <Route path="/parent/dashboard" element={<Navigate to="/app/parent" replace />} />
            <Route path="/subscription" element={<Navigate to="/app/subscription" replace />} />

            {/* ===== AUTH PAGES ===== */}
            <Route path="/login" element={<HomePage />} />
            <Route path="/login/corporate" element={<CorporateLogin />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/parent-login" element={<ParentLoginPage />} />
            <Route path="/approve-parent/:token" element={<ApproveParentLinkPage />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />

            {/* ===== ADMIN PAGES ===== */}
            <Route path="/admin" element={<AdminLoginPage />} />
            <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/user/:userId" element={<ProtectedRoute><UserDetailDashboard /></ProtectedRoute>} />
            <Route path="/admin/exam-bank" element={<ProtectedRoute><AdminExamBank /></ProtectedRoute>} />
            <Route path="/admin/teacher" element={<ProtectedRoute><TeacherPanel /></ProtectedRoute>} />
            <Route path="/admin/study" element={<ProtectedRoute><StudyAdminPanel /></ProtectedRoute>} />
            
            {/* ===== CONTENT PIPELINE ADMIN PAGES ===== */}
            <Route path="/app/admin/content-pipeline" element={<ProtectedRoute><ContentPipelineDashboard /></ProtectedRoute>} />
            <Route path="/app/admin/content-pipeline/detail" element={<ProtectedRoute><ContentPipelineDetail /></ProtectedRoute>} />
            <Route path="/app/admin/content-pipeline/review" element={<ProtectedRoute><ContentPipelineReview /></ProtectedRoute>} />
            <Route path="/app/admin/content-pipeline/coverage" element={<ProtectedRoute><ContentPipelineCoverage /></ProtectedRoute>} />
            
            {/* ===== INSTITUTIONAL / SCHOOL ADMIN PAGES ===== */}
            <Route path="/app/school-admin" element={<OrgProtectedRoute allowedRoles={["admin"]}><SchoolAdminDashboard /></OrgProtectedRoute>} />
            <Route path="/app/school-admin/students" element={<OrgProtectedRoute allowedRoles={["admin"]}><StudentManagement /></OrgProtectedRoute>} />
            <Route path="/app/school-admin/teachers" element={<OrgProtectedRoute allowedRoles={["admin"]}><TeacherManagement /></OrgProtectedRoute>} />
            <Route path="/app/school-admin/classes" element={<OrgProtectedRoute allowedRoles={["admin"]}><ClassManagement /></OrgProtectedRoute>} />
            <Route path="/app/school-admin/analytics" element={<OrgProtectedRoute allowedRoles={["admin"]}><OrgAnalytics /></OrgProtectedRoute>} />
            <Route path="/app/school-admin/settings" element={<OrgProtectedRoute allowedRoles={["admin"]}><OrgSettings /></OrgProtectedRoute>} />
            <Route path="/app/school-admin/messages" element={<OrgProtectedRoute allowedRoles={["admin"]}><SchoolAdminMessages /></OrgProtectedRoute>} />
            
            {/* ===== TEACHER PANEL PAGES ===== */}
            <Route path="/app/teacher" element={<OrgProtectedRoute allowedRoles={["teacher"]}><TeacherDashboard /></OrgProtectedRoute>} />
            <Route path="/app/teacher/class/:classId" element={<OrgProtectedRoute allowedRoles={["teacher"]}><ClassDetail /></OrgProtectedRoute>} />
            <Route path="/app/teacher/messages" element={<OrgProtectedRoute allowedRoles={["teacher"]}><TeacherMessages /></OrgProtectedRoute>} />
            <Route path="/app/teacher/assignments" element={<OrgProtectedRoute allowedRoles={["teacher"]}><TeacherAssignments /></OrgProtectedRoute>} />
            <Route path="/app/teacher/content" element={<OrgProtectedRoute allowedRoles={["teacher"]}><ContentDashboard /></OrgProtectedRoute>} />
            <Route path="/app/teacher/content/notes" element={<OrgProtectedRoute allowedRoles={["teacher"]}><NotesManager /></OrgProtectedRoute>} />
            <Route path="/app/teacher/content/questions" element={<OrgProtectedRoute allowedRoles={["teacher"]}><QuestionBank /></OrgProtectedRoute>} />
            <Route path="/app/teacher/content/exams" element={<OrgProtectedRoute allowedRoles={["teacher"]}><ExamBuilder /></OrgProtectedRoute>} />
            <Route path="/app/teacher/content/flashcards" element={<OrgProtectedRoute allowedRoles={["teacher"]}><FlashcardBuilder /></OrgProtectedRoute>} />
            
            {/* ===== PARENT PANEL PAGES ===== */}
            <Route path="/app/parent/messages" element={<ParentMessages />} />
            
            {/* ===== LEGAL PAGES ===== */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/child-privacy-policy" element={<ChildPrivacyPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/kvkk-policy" element={<KVKKPolicy />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster 
          position="top-right" 
          expand={false}
          richColors
          closeButton
          toastOptions={{
            duration: 2000,
            style: {
              fontSize: '13px',
              padding: '10px 14px',
              maxWidth: '280px',
              minHeight: 'auto',
            },
            className: 'my-toast',
            descriptionClassName: 'my-toast-description',
          }}
        />
        <style>{`
          [data-sonner-toaster] {
            top: 20px !important;
            right: 20px !important;
            left: auto !important;
          }
          [data-sonner-toast] {
            transform: translateX(0) !important;
          }
        `}</style>
      </div>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
